import { useEffect, useState } from "react";
import { runQuery } from "../api.js";
import { useRepo } from "../data/repo.js";
import type { QueryResult } from "../../shared/types.js";
import { ResultTable } from "../viz/ResultTable.js";
import { DATA_INSERT_EVENT } from "../layout/SourceTree.js";

/**
 * The SQL console — now a normal system page (middle column). The schema/tables
 * live in the left SourceTree sidebar; clicking a table there loads a starter
 * query here via the DATA_INSERT_EVENT.
 */
export function DataView() {
  // The scratch console resolves @ref includes against the default repo.
  const repo = useRepo();
  const [sql, setSql] = useState("");
  const [result, setResult] = useState<QueryResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load a query when a table is clicked in the SourceTree.
  useEffect(() => {
    const onInsert = (e: Event): void => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === "string") setSql(detail);
    };
    window.addEventListener(DATA_INSERT_EVENT, onInsert);
    return () => window.removeEventListener(DATA_INSERT_EVENT, onInsert);
  }, []);

  const run = async (): Promise<void> => {
    if (!sql.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await runQuery(repo, sql.trim(), { force: true }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dw-doc dw-data">
      <h1>Data</h1>
      <p className="dw-system-lead">
        Run SQL against any source. Pick a table from the left, or write a query —
        prefix with <code>-- @source: &lt;id&gt;</code> to target a source.
      </p>
      <textarea
        className="dw-textarea dw-sql-editor"
        placeholder="select 1;"
        value={sql}
        onChange={(e) => setSql(e.target.value)}
      />
      <div className="dw-query-actions">
        <button onClick={run} disabled={busy} className="primary">
          {busy ? "Running…" : "Run"}
        </button>
      </div>
      {error && <pre className="dw-error">{error}</pre>}
      {result && <ResultTable result={result} />}
    </div>
  );
}
